import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { User } from '../models/user';
import { RememberStateService } from './remember-state.service';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
    currentUserSubject = new BehaviorSubject<User>(null);

    constructor(
        private http: HttpClient,
        private router: Router,
        private rememberStateService: RememberStateService
    ) {}

    async login(username: string, password: string): Promise<boolean> {
        const data = { username, password };

        try {
            await firstValueFrom(
                this.http.post<any>(`${environment.apiUrl}/authentication/login`, data, { withCredentials: true })
            );
            await this.isJwtAuthenticated();
            return this.isAuthenticatedSubject.getValue();
        } catch (error) {
            this.updateIsAuthenticatedSubject(false);
            this.updateCurrentUser(null);
            throw error.status === 400 ? error.error.validationErrors[0].errorMessage : 'Unknown error';
        }
    }

    async logout(addReturnUrl = true) {
        const isAuthenticated = this.isAuthenticatedSubject.getValue();
        if (isAuthenticated) {
            await firstValueFrom(
                this.http.post<any>(`${environment.apiUrl}/authentication/Logout`, {}, { withCredentials: true })
            );
        }
        this.rememberStateService.clear(['languageId', 'darkMode']);

        this.updateIsAuthenticatedSubject(false);
        this.updateCurrentUser(null);

        const routerOptions = addReturnUrl && isAuthenticated ? { queryParams: { returnUrl: this.router.url } } : {};

        if (this.router.url.includes('/reset-password')) return true;
        return this.router.navigate(['/login'], routerOptions);
    }

    async forgetPassword(username: string): Promise<void> {
        const data = { username };

        try {
            return await firstValueFrom(
                this.http.post<any>(`${environment.apiUrl}/command/ForgotPassword`, data, { withCredentials: true })
            );
        } catch (error) {
            throw error.status === 400 ? error.error.validationErrors[0].errorMessage : 'Unknown error';
        }
    }

    async getPasswordResetStatus(
        username: string,
        passwordResetToken: string,
        isSetPassword: boolean
    ): Promise<boolean> {
        const data = { username, passwordResetToken, isSetPassword };

        try {
            return await firstValueFrom(
                this.http.post<boolean>(`${environment.apiUrl}/query/PasswordResetStatus`, data, {
                    withCredentials: true
                })
            );
        } catch (error) {
            throw error.status === 400 ? error.error.validationErrors[0].errorMessage : 'Unknown error';
        }
    }

    async resetPassword(token: string, newPassword: string, repeatNewPassword: string): Promise<void> {
        const data = { token, newPassword, repeatNewPassword };

        try {
            return await firstValueFrom(
                this.http.post<any>(`${environment.apiUrl}/command/ResetPassword`, data, { withCredentials: true })
            );
        } catch (error) {
            throw error.status === 400 ? error.error.validationErrors[0].errorMessage : 'Unknown error';
        }
    }

    async getCurrentUser(): Promise<User> {
        try {
            const data = await firstValueFrom(
                this.http.get(`${environment.apiUrl}/query/CurrentUser`, { withCredentials: true })
            );
            const user = data ? new User(data) : null;

            this.updateIsAuthenticatedSubject(!!data);
            this.updateCurrentUser(user);

            return user;
        } catch (error) {
            this.updateIsAuthenticatedSubject(false);
            this.updateCurrentUser(null);

            throw error;
        }
    }

    refreshAccessToken() {
        return this.http.post<any>(
            `${environment.apiUrl}/authentication/RefreshAccessToken`,
            {},
            { withCredentials: true }
        );
    }

    private updateIsAuthenticatedSubject(value) {
        if (this.isAuthenticatedSubject.getValue() !== value) {
            this.isAuthenticatedSubject.next(value);
        }
    }

    private updateCurrentUser(user: User) {
        if (this.currentUserSubject.getValue() !== user) {
            this.currentUserSubject.next(user);
        }
    }

    async isJwtAuthenticated(): Promise<void> {
        try {
            const result = await firstValueFrom(
                this.http.post<any>(`${environment.apiUrl}/authentication/IsAuth`, {}, { withCredentials: true })
            );
            this.updateIsAuthenticatedSubject(result);
        } catch (err) {
            this.updateIsAuthenticatedSubject(false);
        }
    }
}
